import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  RefreshCwIcon,
  Loader2Icon,
  CheckCircleIcon,
  AlertTriangleIcon,
  XCircleIcon,
  PlayIcon,
  ClockIcon,
  BuildingIcon,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  MobilePage,
} from '../components/mobile'

// ── Types ─────────────────────────────────────────────────────────────────────

interface PropertyDetail {
  property_id: string
  address: string
  records_upserted: number
  sources: Record<string, number>
  error: string | null
}

interface ProgressLogEntry {
  ts: string
  level: 'info' | 'ok' | 'warn' | 'error' | string
  message: string
}

interface SyncLog {
  id: string
  run_type: string
  triggered_by: string | null
  started_at: string
  finished_at: string | null
  properties_total: number
  properties_ok: number
  properties_error: number
  records_upserted: number
  current_address: string | null
  updated_at: string | null
  progress_log?: ProgressLogEntry[] | null
  details: PropertyDetail[]
  created_at: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PROXY = '' // same origin via CRA proxy
const POLL_MS = 3000

const formatDate = (d: string | null) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

const getDuration = (start: string, end: string | null) => {
  if (!end) return 'Running…'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

const SOURCE_LABELS: Record<string, string> = {
  hpd_complaints:  'HPD Complaints',
  hpd_violations:  'HPD Violations',
  dob_ecb:         'DOB/ECB',
  dsny_oath:       'DSNY/OATH',
  oath_ecb:        'OATH/ECB (other agencies)',
  complaint_311:   '311 Complaints',
  pluto:           'PLUTO',
  acris:           'ACRIS',
}

const processedCount = (log: SyncLog) =>
  (log.properties_ok || 0) + (log.properties_error || 0)

const progressPct = (log: SyncLog) => {
  const total = log.properties_total || 0
  if (total <= 0) return 0
  return Math.min(100, Math.round((processedCount(log) / total) * 100))
}

const formatLogTime = (ts: string) => {
  try {
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
    })
  } catch {
    return ''
  }
}

const progressLogLineClass = (level: string) => {
  if (level === 'error') return 'text-red-400'
  if (level === 'warn') return 'text-amber-300'
  if (level === 'ok') return 'text-emerald-400'
  return 'text-gray-300'
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CityRecordsSyncLogs() {
  const { user } = useAuth()
  const [logs, setLogs] = useState<SyncLog[]>([])
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const liveLogRef = useRef<HTMLDivElement | null>(null)

  const fetchLogs = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true)
      const resp = await fetch(`${PROXY}/api/city-records/sync-logs?limit=50`)
      const json = await resp.json()
      if (!resp.ok) throw new Error(json.error || 'Failed to load logs')
      setLogs(json.logs || [])
    } catch (err: any) {
      console.error('Error fetching sync logs:', err)
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }, [])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  const inProgress = logs.find(l => !l.finished_at) || null
  const isRunning = Boolean(inProgress) || starting

  // Poll while a sync is in progress so the progress bar stays live
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (!inProgress && !starting) return

    pollRef.current = setInterval(() => {
      fetchLogs({ silent: true })
    }, POLL_MS)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [inProgress?.id, starting, fetchLogs])

  // Clear "starting" once the in-progress row appears
  useEffect(() => {
    if (inProgress && starting) setStarting(false)
  }, [inProgress, starting])

  // Keep the live console pinned to the latest line
  useEffect(() => {
    const el = liveLogRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [inProgress?.progress_log?.length, inProgress?.id])

  const handleManualRun = async () => {
    if (isRunning) return
    setShowConfirmModal(false)
    setStarting(true)
    setMessage(null)
    try {
      const resp = await fetch(`${PROXY}/api/city-records/sync-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ triggered_by: user?.email || 'manual' }),
      })
      const json = await resp.json()
      if (!resp.ok) throw new Error(json.error || 'Failed to start sync')
      setMessage({ type: 'success', text: 'City records sync started. Progress updates below.' })
      // Pull the new log row quickly; interval polling takes over after
      setTimeout(() => fetchLogs({ silent: true }), 800)
      setTimeout(() => fetchLogs({ silent: true }), 2500)
    } catch (err: any) {
      setStarting(false)
      setMessage({ type: 'error', text: 'Failed to start sync: ' + (err.message || 'Unknown error') })
    } finally {
      setTimeout(() => setMessage(null), 12000)
    }
  }

  const getRunTypeBadge = (runType: string) => {
    if (runType === 'nightly')
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
          <ClockIcon className="w-3 h-3" /> Nightly
        </span>
      )
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <PlayIcon className="w-3 h-3" /> Manual
      </span>
    )
  }

  const getStatusIcon = (log: SyncLog) => {
    if (!log.finished_at) return <Loader2Icon className="w-5 h-5 text-blue-500 animate-spin" />
    if (log.properties_error > 0) return <AlertTriangleIcon className="w-5 h-5 text-amber-500" />
    return <CheckCircleIcon className="w-5 h-5 text-green-500" />
  }

  return (
    <MobilePage>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">City Records Sync</h1>
          <p className="mt-1 text-gray-600">
            Nightly refresh of 311, HPD, ECB, DSNY, PLUTO &amp; ACRIS records for all properties.
            Runs automatically at 2:00 AM.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchLogs()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCwIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={isRunning}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            {isRunning
              ? <><Loader2Icon className="w-4 h-4 animate-spin" /> Running…</>
              : <><PlayIcon className="w-4 h-4" /> Run Now</>}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Live progress while a run is in flight */}
      {(inProgress || starting) && (
        <div className="bg-white rounded-lg border border-blue-200 p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Loader2Icon className="w-5 h-5 text-blue-500 animate-spin" />
              <h2 className="text-sm font-semibold text-gray-900">Sync in progress</h2>
            </div>
            {inProgress && (
              <span className="text-sm font-medium text-blue-700">
                {progressPct(inProgress)}%
              </span>
            )}
          </div>

          <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{
                width: inProgress
                  ? `${Math.max(progressPct(inProgress), inProgress.properties_total ? 2 : 0)}%`
                  : '4%',
              }}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: 'Processed',
                value: inProgress
                  ? `${processedCount(inProgress)} / ${inProgress.properties_total || '—'}`
                  : 'Starting…',
              },
              {
                label: 'OK',
                value: inProgress ? String(inProgress.properties_ok || 0) : '—',
              },
              {
                label: 'Errors',
                value: inProgress ? String(inProgress.properties_error || 0) : '—',
                alert: Boolean(inProgress && inProgress.properties_error > 0),
              },
              {
                label: 'Records',
                value: inProgress ? (inProgress.records_upserted || 0).toLocaleString() : '—',
              },
            ].map(({ label, value, alert }) => (
              <div key={label} className={`rounded-lg border p-3 ${alert ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`text-base font-semibold mt-0.5 ${alert ? 'text-amber-700' : 'text-gray-900'}`}>{value}</p>
              </div>
            ))}
          </div>

          {inProgress?.current_address && (
            <p className="text-sm text-gray-600 truncate">
              <span className="text-gray-400">Current: </span>
              {inProgress.current_address}
            </p>
          )}

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1.5">Live log</p>
            <div
              ref={liveLogRef}
              className="h-40 overflow-y-auto rounded-md border border-gray-800 bg-gray-950 px-3 py-2 font-mono text-[11px] leading-5"
            >
              {(inProgress?.progress_log || []).length === 0 ? (
                <p className="text-gray-500">Waiting for activity…</p>
              ) : (
                (inProgress?.progress_log || []).map((line, i) => (
                  <div key={`${line.ts}-${i}`} className={`whitespace-pre-wrap break-all ${progressLogLineClass(line.level)}`}>
                    <span className="text-gray-500 mr-2">{formatLogTime(line.ts)}</span>
                    <span className="uppercase mr-2 opacity-70">{line.level}</span>
                    {line.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats strip from most recent finished run */}
      {logs.length > 0 && logs[0].finished_at && !inProgress && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Last run', value: formatDate(logs[0].started_at) },
            { label: 'Properties synced', value: `${logs[0].properties_ok} / ${logs[0].properties_total}` },
            { label: 'Records upserted', value: logs[0].records_upserted.toLocaleString() },
            { label: 'Errors', value: logs[0].properties_error, alert: logs[0].properties_error > 0 },
          ].map(({ label, value, alert }) => (
            <div key={label} className={`bg-white rounded-lg border p-4 ${alert ? 'border-amber-300' : 'border-gray-200'}`}>
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`text-lg font-semibold mt-1 ${alert ? 'text-amber-600' : 'text-gray-900'}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Log table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <Loader2Icon className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <BuildingIcon className="w-8 h-8 mb-2" />
            <p>No sync runs yet. Click <strong>Run Now</strong> to kick off the first one.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Properties</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Records</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Triggered by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map(log => {
                const running = !log.finished_at
                const done = processedCount(log)
                return (
                  <React.Fragment key={log.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                    >
                      <td className="px-4 py-3">{getStatusIcon(log)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{formatDate(log.started_at)}</td>
                      <td className="px-4 py-3">{getRunTypeBadge(log.run_type)}</td>
                      <td className="px-4 py-3 text-gray-600">{getDuration(log.started_at, log.finished_at)}</td>
                      <td className="px-4 py-3">
                        {running ? (
                          <span className="text-blue-600 font-medium">
                            {done} / {log.properties_total || '…'}
                            {log.properties_error > 0 && (
                              <span className="text-amber-600 ml-1">({log.properties_error} err)</span>
                            )}
                          </span>
                        ) : (
                          <>
                            <span className="text-green-600 font-medium">{log.properties_ok}</span>
                            {log.properties_error > 0 && (
                              <span className="text-amber-600 ml-1">/ {log.properties_error} err</span>
                            )}
                            <span className="text-gray-400 ml-1">of {log.properties_total}</span>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{(log.records_upserted || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-500">{log.triggered_by || '—'}</td>
                    </tr>

                    {/* Expanded detail rows */}
                    {expandedRow === log.id && (
                      <tr>
                        <td colSpan={7} className="bg-gray-50 px-6 py-4">
                          {running && (
                            <p className="text-xs text-blue-600 mb-3">
                              Per-property details appear when this run finishes.
                              {log.current_address ? ` Currently: ${log.current_address}` : ''}
                            </p>
                          )}
                          <p className="text-xs font-semibold text-gray-500 mb-3 uppercase">Per-property breakdown</p>
                          {(log.details || []).length === 0 ? (
                            <p className="text-sm text-gray-400">No property details yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {(log.details || []).map((d, i) => (
                                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                                  d.error ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
                                }`}>
                                  {d.error
                                    ? <XCircleIcon className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                                    : <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />}
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{d.address}</p>
                                    {d.error
                                      ? <p className="text-xs text-red-600 mt-0.5">{d.error}</p>
                                      : (
                                        <div className="flex flex-wrap gap-2 mt-1">
                                          {Object.entries(d.sources || {}).map(([src, count]) =>
                                            count > 0 ? (
                                              <span key={src} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">
                                                {SOURCE_LABELS[src] || src}: {count}
                                              </span>
                                            ) : null
                                          )}
                                          {d.records_upserted === 0 && (
                                            <span className="text-xs text-gray-400">No records found</span>
                                          )}
                                        </div>
                                      )
                                    }
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Run City Records Sync Now?</h3>
            <p className="text-sm text-gray-600 mb-6">
              This will query 311, HPD, DOB/ECB, DSNY, PLUTO and ACRIS for every property in the system.
              It runs in the background and may take a few minutes. You can watch live progress on this page.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleManualRun}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Yes, Run Now
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
